// AGC: RegisterPage - User registration component
// AGC: Allows new users to create an account
// AGC: Features video background with glassmorphic card overlay

import React, { useState, useEffect } from 'react'
import { useAuth } from '../../hooks'

interface RegisterPageProps {
  onNavigateToLogin: () => void
  // AGC: Optional pre-fill from team invitation — locks email field
  prefill?: {
    email?: string
    displayName?: string
    phone?: string
  }
}

/**
 * AGC: RegisterPage - Account creation screen
 * AGC: Features video background and glassmorphic registration card
 */
export const RegisterPage: React.FC<RegisterPageProps> = ({ onNavigateToLogin, prefill }) => {
  const {
    isLoading,
    error,
    clearError,
    register,
    loginWithGoogle,
    loginWithApple,
  } = useAuth()

  // AGC: Form state — pre-fill from invitation if available
  const [displayName, setDisplayName] = useState(prefill?.displayName || '')
  const [email, setEmail] = useState(prefill?.email || '')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [phoneNumber, setPhoneNumber] = useState(prefill?.phone?.replace(/\D/g, '').replace(/^55/, '') || '')
  const [showPassword, setShowPassword] = useState(false)
  const [acceptTerms, setAcceptTerms] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)

  // AGC: Force sync prefill data — runs on mount and whenever prefill changes
  useEffect(() => {
    if (!prefill) return
    if (prefill.displayName) setDisplayName(prefill.displayName)
    if (prefill.email) setEmail(prefill.email)
    if (prefill.phone) setPhoneNumber(prefill.phone.replace(/\D/g, '').replace(/^55/, ''))
  }, [prefill])

  // AGC: Validate form
  const validateForm = (): boolean => {
    setValidationError(null)

    if (!displayName.trim()) {
      setValidationError('Nome é obrigatório')
      return false
    }

    if (!email.trim()) {
      setValidationError('Email é obrigatório')
      return false
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setValidationError('Email inválido')
      return false
    }

    if (password.length < 6) {
      setValidationError('Senha deve ter pelo menos 6 caracteres')
      return false
    }

    if (password !== confirmPassword) {
      setValidationError('As senhas não coincidem')
      return false
    }

    if (!acceptTerms) {
      setValidationError('Você deve aceitar os termos de uso')
      return false
    }

    return true
  }

  // AGC: Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return

    // AGC: Format phone number if provided
    let formattedPhone: string | undefined
    if (phoneNumber) {
      formattedPhone = '+55' + phoneNumber.replace(/\D/g, '')
    }

    await register({
      email,
      password,
      displayName,
      phoneNumber: formattedPhone,
    })
  }

  // AGC: Password strength indicator
  const getPasswordStrength = (): { label: string; color: string; width: string } => {
    if (password.length === 0) return { label: '', color: 'bg-gray-200', width: '0%' }
    if (password.length < 6) return { label: 'Fraca', color: 'bg-red-500', width: '25%' }
    if (password.length < 8) return { label: 'Razoável', color: 'bg-yellow-500', width: '50%' }
    if (password.length < 10 || !/[A-Z]/.test(password) || !/[0-9]/.test(password)) {
      return { label: 'Boa', color: 'bg-blue-500', width: '75%' }
    }
    return { label: 'Forte', color: 'bg-green-500', width: '100%' }
  }

  const passwordStrength = getPasswordStrength()

  // AGC: Common input styles for high contrast
  const inputClassName = "w-full px-4 py-3 bg-white text-gray-900 border border-gray-300 rounded-xl placeholder:text-gray-400 focus:ring-2 focus:ring-[var(--accent-primary)] focus:border-transparent"

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* AGC: Video Background */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
      >
        <source src="/videos/bg.mp4" type="video/mp4" />
      </video>
      
      {/* AGC: Dark overlay for text readability */}
      <div className="absolute inset-0 bg-black/50" />
      
      {/* AGC: Content */}
      <div className="relative z-10 flex items-center justify-center min-h-screen p-4 py-8">
        <div className="w-full max-w-md">
          {/* AGC: Title only - no logo */}
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-white drop-shadow-lg">DroidFarm</h1>
            <p className="text-white/80 mt-1 drop-shadow">Criar Conta</p>
          </div>

          {/* AGC: Register Card - Glassmorphic style */}
          <div className="bg-white/90 backdrop-blur-md rounded-2xl shadow-2xl p-6 border border-white/20">
            {/* AGC: Error message */}
            {(error || validationError) && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex justify-between items-center">
                <span>{validationError || error}</span>
                <button 
                  onClick={() => {
                    clearError()
                    setValidationError(null)
                  }} 
                  className="text-red-500 hover:text-red-700"
                >
                  ✕
                </button>
              </div>
            )}

            {/* AGC: Social registration buttons */}
            <div className="space-y-3 mb-6">
              <button
                onClick={loginWithGoogle}
                disabled={isLoading}
                className="w-full py-3 px-4 bg-white border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-3 disabled:opacity-50"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Criar conta com Google
              </button>

              <button
                onClick={loginWithApple}
                disabled={isLoading}
                className="w-full py-3 px-4 bg-black text-white rounded-xl font-medium hover:bg-gray-800 transition-colors flex items-center justify-center gap-3 disabled:opacity-50"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="white">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                Criar conta com Apple
              </button>
            </div>

            {/* AGC: Divider */}
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white/90 text-gray-500">ou cadastre-se com email</span>
              </div>
            </div>

            {/* AGC: Registration form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* AGC: Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome Completo *
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="João Silva"
                  className={inputClassName}
                  required
                />
              </div>

              {/* AGC: Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => !prefill?.email && setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  className={`${inputClassName} ${prefill?.email ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                  readOnly={!!prefill?.email}
                  required
                />
              </div>

              {/* AGC: Phone (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefone (opcional)
                </label>
                <div className="flex gap-2">
                  <div className="px-4 py-3 bg-gray-100 border border-gray-300 rounded-xl text-gray-600">
                    +55
                  </div>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                    placeholder="41 99999-9999"
                    className={`flex-1 ${inputClassName}`}
                  />
                </div>
              </div>

              {/* AGC: Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Senha *
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className={`${inputClassName} pr-12`}
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
                
                {/* AGC: Password strength bar */}
                {password.length > 0 && (
                  <div className="mt-2">
                    <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${passwordStrength.color} transition-all duration-300`}
                        style={{ width: passwordStrength.width }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Força da senha: <span className="font-medium">{passwordStrength.label}</span>
                    </p>
                  </div>
                )}
              </div>

              {/* AGC: Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirmar Senha *
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className={`${inputClassName} ${
                    confirmPassword && password !== confirmPassword 
                      ? 'border-red-300 bg-red-50' 
                      : ''
                  }`}
                  required
                />
                {confirmPassword && password !== confirmPassword && (
                  <p className="text-xs text-red-500 mt-1">As senhas não coincidem</p>
                )}
              </div>

              {/* AGC: Terms checkbox */}
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="terms"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  className="mt-1 h-4 w-4 border-gray-300 rounded"
                  style={{ accentColor: 'var(--accent-primary)' }}
                />
                <label htmlFor="terms" className="text-sm text-gray-600">
                  Eu aceito os{' '}
                  <a href="#" className="hover:opacity-80" style={{ color: 'var(--accent-dark-text)' }}>
                    Termos de Uso
                  </a>{' '}
                  e a{' '}
                  <a href="#" className="hover:opacity-80" style={{ color: 'var(--accent-dark-text)' }}>
                    Política de Privacidade
                  </a>
                </label>
              </div>

              {/* AGC: Submit button */}
              <button
                type="submit"
                disabled={isLoading || !acceptTerms}
                className="w-full py-3 px-4 text-white rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: 'var(--accent-primary)' }}
              >
                {isLoading ? 'Criando conta...' : 'Criar Conta'}
              </button>
            </form>

            {/* AGC: Login link */}
            <div className="mt-6 text-center">
              <p className="text-gray-600">
                Já tem uma conta?{' '}
                <button
                  onClick={onNavigateToLogin}
                  className="font-medium hover:opacity-80"
                  style={{ color: 'var(--accent-dark-text)' }}
                >
                  Entrar
                </button>
              </p>
            </div>
          </div>

          {/* AGC: Footer */}
          <p className="text-center text-white/70 text-sm mt-6 drop-shadow">
            © 2025 DroidFarm - Gestão Agrícola
          </p>
        </div>
      </div>
    </div>
  )
}

export default RegisterPage
